import { ReactNode, useEffect, useState } from "react"
import { Navigate, useLocation, useSearchParams } from "react-router"
import { useQueryClient } from "@tanstack/react-query"
import cx from "classnames"

import { Spinner } from "../../components/Spinner"
import FireworksConfetti from "../../components/FireworksConfetti"
import { VerifyEmailInnerContent } from "../../sections/VerifyEmailContent"
import AcceptTocModal from "../../sections/AcceptTocModal"
import {
  useAccount,
  useAccountId,
  useCreateMembership,
  useProfile,
  useAccountProperty,
  useAccountUrlPrefix,
  useIsMultiAccount,
  useAccountInterest,
  useCheckoutStatus,
  useQueryParam,
} from "../../hooks"
import { useScrubQuerystring } from "../../hooks/useScrubQuerystring"
import { useIsCheckoutSuccess } from "../../hooks/useIsCheckoutSuccess"
import { heapTrackEvent } from "../../api/integrations"
import { useAccessToken, useSessionStore } from "../../stores"
import { AccountProperty } from "../../types/property"
import { Profile } from "../../types"
import { AccountTypes } from "@/types/account"
import { AccountInterestTypes } from "@/types/accountInterest"
import { getQueryParam } from "../../shared/utils"

export interface MainLayoutProps {
  children: ReactNode
  isLoading?: boolean | boolean[]
  contentBg?: string
  loader?: ReactNode
  className?: string
}

const MainLayout = ({
  children,
  isLoading,
  contentBg,
  loader,
  className,
}: MainLayoutProps) => {
  const bgClass = contentBg
  const location = useLocation()
  const queryClient = useQueryClient()
  const accessToken = useAccessToken()
  const accountId = useAccountId()
  const isMultiAccount = useIsMultiAccount()
  const accountUrlPrefix = useAccountUrlPrefix()
  const [searchParams] = useSearchParams()
  const isLoggedIn = !!accessToken
  const stripeSessionId = useQueryParam("session_id")
  const isCheckoutSuccess = useIsCheckoutSuccess()
  const scrubQuerystring = useScrubQuerystring({ paramToScrub: "session_id" })
  const { setIsMember } = useSessionStore()
  // Other sites (not the landowner platform) may send users to the landowner platform
  // to sign up / log in /  update their property boundaries. After login/property submission,
  // we'll redirect them back to the site they came from.
  // https://ncx.slab.com/posts/vibe-coding-an-app-576lh2ug
  //
  // Note `redirect` may also be stored in `location.state` if user was redirected
  // to login page in `App.tsx`
  const redirect =
    useQueryParam("redirect") ||
    (location?.state?.from &&
      getQueryParam(location?.state?.from as Location, "redirect"))

  const [isEmbeddedSuccess, setIsEmbeddedSuccess] = useState(false)

  const queryEnabled =
    isLoggedIn &&
    ((isMultiAccount && accountId !== "_single") || !isMultiAccount)

  // Fetch data
  const { data: profile, isLoading: profileIsLoading } = useProfile<
    Profile,
    Error
  >(queryClient, {
    enabled: isLoggedIn,
  })

  const {
    data: account,
    isLoading: accountIsLoading,
    refetch: refetchAccount,
  } = useAccount<AccountTypes, Error>(queryClient, accountId, {
    enabled: queryEnabled,
  })

  const { data: property, isLoading: propertyIsLoading } = useAccountProperty<
    AccountProperty,
    Error
  >(queryClient, accountId, {
    enabled: queryEnabled,
  })

  const { data: accountInterestData, isLoading: accountInterestIsLoading } =
    useAccountInterest<AccountInterestTypes, Error>(queryClient, accountId, {
      enabled: queryEnabled,
    })

  const { data: checkoutStatusData } = useCheckoutStatus(
    accountId,
    stripeSessionId || "",
    {
      enabled: !!accountId && !!stripeSessionId,
    }
  )

  useEffect(() => {
    if (checkoutStatusData?.status === "complete") {
      scrubQuerystring()
      setIsEmbeddedSuccess(true)
      heapTrackEvent("stripe-embedded-payment-success")
    }
  }, [checkoutStatusData?.status, scrubQuerystring])

  // Handle membership creation
  const membershipSuccessToken =
    searchParams.get("checkout-success")?.slice(2, -1) || ""

  const { status: membershipStatus } = useCreateMembership(
    accountId,
    membershipSuccessToken || "",
    stripeSessionId || "",
    {
      enabled: !!accountId && (!!membershipSuccessToken || isEmbeddedSuccess),
    }
  )

  useEffect(() => {
    if (account?.is_member !== undefined) {
      setIsMember(account?.is_member)
    }
  }, [account?.is_member, setIsMember])

  useEffect(() => {
    if (membershipStatus === "success") {
      refetchAccount().then((result) => {
        if (result.data) {
          setIsMember(result.data.is_member ?? false)
        }
      })
    }
  }, [membershipStatus, refetchAccount, setIsMember])

  const createMembershipIsLoading =
    membershipSuccessToken && membershipStatus === "pending"

  // Determine loading state
  const isLoadingArr = [
    profileIsLoading,
    accountIsLoading,
    propertyIsLoading,
    createMembershipIsLoading,
    accountInterestIsLoading,
    ...(Array.isArray(isLoading) ? isLoading : [isLoading]),
  ]

  if (isLoadingArr.some(Boolean)) {
    return (
      loader ?? (
        <div className="flex-auto flex items-center justify-center">
          <Spinner />
        </div>
      )
    )
  }

  // Redirection Conditions
  const shouldRedirectToPropertyBoundaries =
    account &&
    property &&
    !property.bounds?.coordinates?.length &&
    !location.pathname.includes("/property-boundaries") &&
    location.pathname !== "/logout"

  const shouldRedirectToOnboardingGoals =
    account &&
    accountInterestData &&
    !accountInterestData.has_answered_all_questions &&
    !location.pathname.includes("/become-a-member") &&
    !location.pathname.includes("/property-boundaries") &&
    !location.pathname.includes("/onboarding/goals") &&
    location.pathname !== "/logout"

  if (shouldRedirectToPropertyBoundaries) {
    // Preserve any location state from a previous redirect. This
    // ensures that a user who completes:
    // 1. Visits landowners.ncx.com/?redirect=/some-other-site but
    //    is redirected to `/getting-started` login page, then
    // 2. Completes login and is here redirected to `/onboarding/property-boundaries`
    // will still have the `search` location state set to `?redirect=/some-other-site`,
    // enabling us to redirect them after property submission.
    // If there is no preexisting location state, save current location as `from`
    const newLocationState = location.state
      ? { ...location.state }
      : { from: location }

    return (
      <Navigate
        replace
        to={
          isMultiAccount
            ? `${accountUrlPrefix}/settings/property-boundaries`
            : `/onboarding/property-boundaries`
        }
        state={newLocationState}
      />
    )
  } else if (
    redirect &&
    typeof redirect === "string" &&
    !location.pathname.includes("/property-boundaries")
  ) {
    // If user arrived here with a redirect query parameter,
    // redirect them to the original site after login or property submission
    // (not while they're on the property boundaries page)
    window.location.replace(redirect)
    return null
  }

  if (shouldRedirectToOnboardingGoals) {
    return <Navigate replace to={`${accountUrlPrefix}/onboarding/goals`} />
  }

  if (!isLoggedIn) {
    return (
      <div className={cx("flex-auto flex flex-col", bgClass, className)}>
        {children}
      </div>
    )
  }

  return (
    <div className={cx("flex-auto flex flex-col", bgClass, className)}>
      <VerifyEmailInnerContent profile={profile as Profile}>
        {children}
      </VerifyEmailInnerContent>

      <AcceptTocModal profile={profile} />

      <FireworksConfetti
        show={isCheckoutSuccess || isEmbeddedSuccess}
        speed={3}
        duration={3000}
      />
    </div>
  )
}

export default MainLayout
